import { Builder } from "@builder.io/react";
import type { VariantProps } from "class-variance-authority";
import { cva } from "class-variance-authority";

import type { TextProps } from "@flare/ui/components";
import { Text } from "@flare/ui/components";
import { cn } from "@flare/ui/utils";

import { V2_TEXT } from "@/builder/component-names";

import { textColors, textVariants } from "../../consts";
import type { BuilderAttributes } from "../../types";

const builderTextVariants = cva("font-sans", {
  variants: {
    color: {
      blue: "text-primary",
      beige: "text-primary-inverted",
      green: "text-action",
      white: "text-primary-inverted",
      gray: "text-disclaimer",
      primary: "text-primary",
      "primary-inverted": "text-primary-inverted",
      secondary: "text-secondary",
      disclaimer: "text-disclaimer",
      action: "text-action",
      success: "text-success",
      "success-highlight": "text-success-highlight",
      warning: "text-warning",
      "warning-highlight": "text-warning-highlight",
      danger: "text-danger",
      "danger-highlight": "text-danger-highlight",
      info: "text-info",
      "info-highlight": "text-info-highlight",
      disabled: "text-disabled",
    },
    variant: {
      "text-1":
        "font-light font-serif text-[16px] leading-[26px] md:text-[24px] md:leading-[36px]",
      "text-2":
        "text-[16px] font-medium leading-[24px] md:text-[18px] md:leading-[28px]",
      "text-3":
        "text-[16px] font-normal leading-[24px] md:text-[18px] md:leading-[28px]",
      "text-4": "text-[16px] font-normal leading-[24px]",
      "text-5": "text-[14px] font-normal leading-[20px]",
      "text-6": "text-[12px] font-normal leading-[20px]",
      label:
        "text-[10px] font-semibold uppercase leading-[18px] tracking-[1.2px] md:text-[14px] md:leading-[22px] md:tracking-[1.6px]",
    },
  },
});

export type BuilderTextProps = Omit<TextProps, "variant" | "color"> &
  VariantProps<typeof builderTextVariants> & {
    attributes?: BuilderAttributes;
    balanceText?: boolean;
  };

/**
 * This component is only registered in Builder.io for Marketing purposes,
 * which is why it has different styling compared to the standard UI components.
 *
 * The styling variants are specifically designed to meet marketing needs,
 * with different font sizes, weights, and line heights optimized for
 * promotional content and marketing materials.
 */
export const BuilderText = ({
  alignment = "start",
  as = "p",
  balanceText,
  children,
  color = "primary",
  maxLines,
  variant = "text-1",
  attributes,
  dangerouslySetInnerHTML,
}: BuilderTextProps) => {
  const { key, className, ...restAttributes } = attributes || {};
  return (
    <Text
      alignment={alignment}
      as={as}
      textWrap={balanceText ? "balance" : undefined}
      maxLines={maxLines}
      key={key}
      className={cn(builderTextVariants({ color, variant }), className)}
      dangerouslySetInnerHTML={dangerouslySetInnerHTML}
      {...restAttributes}
    >
      {children}
    </Text>
  );
};

Builder.registerComponent(BuilderText, {
  name: V2_TEXT,
  noWrap: true,
  inputs: [
    {
      name: "balanceText",
      type: "boolean",
      defaultValue: false,
      helperText:
        "Balances the number of characters on each line, enhancing layout quality and legibility.",
    },
    {
      name: "children",
      friendlyName: "Text",
      type: "string",
      defaultValue: "Text placeholder",
      helperText: "Displayed text.",
    },
    {
      name: "as",
      friendlyName: "Tag",
      type: "string",
      defaultValue: "p",
      enum: ["p", "span"],
      helperText: "HTML tag to be used.",
    },
    {
      name: "variant",
      type: "string",
      defaultValue: "text-1",
      enum: [...textVariants],
      helperText: "Variant of the text.",
    },
    {
      name: "color",
      type: "string",
      defaultValue: "primary",
      enum: [...textColors],
      helperText:
        "Color of the text. The colors blue, beige, green, white, gray and light-gray are deprecated, use semantic colors instead.",
    },
    {
      name: "newText",
      type: "boolean",
      defaultValue: true,
      helperText:
        "Indicates if the text is a new text (This will be removed in the future).",
    },
  ],
});
